import React, { lazy, ComponentType, LazyExoticComponent } from 'react';

/**
 * Robust Lazy Loader with exponential backoff retry mechanism and fallback component
 * Prevents "Failed to fetch dynamically imported module" errors during server restarts or transient network drops.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T } | { [key: string]: any }>,
  retries = 3
): LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError: any;
    
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        const module = await factory();
        if (module && 'default' in module && module.default) {
          return { default: module.default as T };
        }
        const firstExport = Object.values(module || {}).find((v) => typeof v === 'function');
        if (firstExport) {
          return { default: firstExport as T };
        }
        return { default: ((module as any)?.default || module) as T };
      } catch (error: any) {
        lastError = error;
        console.warn(`[lazyWithRetry] Attempt ${attempt + 1}/${retries} failed:`, error);
        // Wait with progressive backoff (400ms, 1000ms, 2000ms)
        const delay = Math.min(400 * Math.pow(2, attempt), 2500);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    // Check if this is a dynamic import chunk error
    const isChunkLoadError = 
      lastError?.message?.includes('Failed to fetch dynamically imported module') ||
      lastError?.name === 'ChunkLoadError' ||
      lastError?.message?.includes('dynamically imported module') ||
      lastError?.message?.includes('Loading chunk') ||
      lastError?.message?.includes('fetch');

    if (isChunkLoadError && typeof window !== 'undefined') {
      const storageKey = 'djp_chunk_reload_ts';
      const lastReload = sessionStorage.getItem(storageKey);
      const now = Date.now();
      
      // If we haven't auto-reloaded in the last 15 seconds, reload cleanly to fetch the fresh bundle
      if (!lastReload || now - parseInt(lastReload, 10) > 15000) {
        sessionStorage.setItem(storageKey, String(now));
        window.location.reload();
        return new Promise<{ default: T }>(() => {});
      }
    }

    // Safe fallback UI component instead of crashing entire React app
    const FallbackComponent: React.FC<any> = () => {
      return React.createElement(
        'div',
        { className: 'p-8 text-center bg-white rounded-3xl border border-slate-200 shadow-sm max-w-lg mx-auto my-12 space-y-4' },
        React.createElement('div', { className: 'w-12 h-12 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center mx-auto text-xl font-bold' }, '!'),
        React.createElement('h3', { className: 'text-base font-bold text-slate-800' }, 'Módulo em atualização'),
        React.createElement('p', { className: 'text-xs text-slate-500 leading-relaxed' }, 'Este módulo está sendo sincronizado com a versão mais recente. Por favor, recarregue a página.'),
        React.createElement(
          'button',
          {
            type: 'button',
            onClick: () => window.location.reload(),
            className: 'px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl shadow-xs transition-colors cursor-pointer'
          },
          'Recarregar Página'
        )
      );
    };

    return { default: FallbackComponent as unknown as T };
  });
}

